import React, { useEffect } from 'react';
import { ToastType, ToastPosition } from '@/types/toast';

// Re-export for backward compatibility
export { ToastType, ToastPosition };

export interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  position?: ToastPosition;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type = ToastType.Info,
  duration = 5000,
  onClose,
  position = ToastPosition.TopRight,
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]); // Removed onClose from dependencies to prevent timer reset

  const positionClasses: Record<ToastPosition, string> = {
    [ToastPosition.TopRight]: 'top-8 right-8',
    [ToastPosition.TopLeft]: 'top-8 left-8',
    [ToastPosition.BottomRight]: 'bottom-8 right-8',
    [ToastPosition.BottomLeft]: 'bottom-8 left-8',
  };

  const typeConfig: Record<ToastType, { bg: string; icon: string }> = {
    [ToastType.Info]: {
      bg: 'bg-blue-600',
      icon: 'ℹ️',
    },
    [ToastType.Success]: {
      bg: 'bg-green-600',
      icon: '✓',
    },
    [ToastType.Warning]: {
      bg: 'bg-yellow-600',
      icon: '⚠️',
    },
    [ToastType.Error]: {
      bg: 'bg-red-600',
      icon: '✕',
    },
  };

  const config = typeConfig[type];

  return (
    <div className={`fixed ${positionClasses[position]} z-50 animate-slide-up`}>
      <div className={`${config.bg} text-white px-6 py-4 rounded-lg shadow-2xl max-w-md flex items-start gap-3`}>
        <span className="text-2xl">{config.icon}</span>
        <div className="flex-1">
          <p className="font-semibold">{type.charAt(0).toUpperCase() + type.slice(1)}</p>
          <p className="text-sm mt-1 text-white/90">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors text-xl leading-none"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default Toast;
